import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { WEEKDAY_LABEL, type EntityCalendar } from '@xiltrion/entidad';
import { EstadoCargando, EstadoError, EstadoVacio } from '@xiltrion/ui/bloques/estados';
import { SelectorFecha, fechaAIso } from '@xiltrion/ui/bloques/selectores';
import { Alert, AlertDescription } from '@xiltrion/ui/alert';
import { Badge } from '@xiltrion/ui/badge';
import { Button } from '@xiltrion/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@xiltrion/ui/card';
import { Icono } from '@xiltrion/ui/icono';
import { Input } from '@xiltrion/ui/input';
import { avisar } from '@xiltrion/ui/sonner';
import { Spinner } from '@xiltrion/ui/spinner';
import { Switch } from '@xiltrion/ui/switch';
import { apiPut, errorMessage } from '@/api/client';
import { Cambio, useEntitySettings } from './EntityProfileTab';

/** Lunes primero, como en un calendario de oficina. */
const WEEK = [1, 2, 3, 4, 5, 6, 0];
type Window = EntityCalendar['workingDays'][number];

/**
 * Calendario hábil de la entidad (GUIA-AGENTES.md §3.11): jornada por día y festivos. Es el calendario por defecto de
 * todas las apps (plazos, vencimientos, días hábiles); la zona horaria es la de la ficha.
 */
export function EntityCalendarTab({ slug }: { slug: string }) {
  const qc = useQueryClient();
  const q = useEntitySettings(slug);
  const [cal, setCal] = useState<EntityCalendar | null>(null);
  const [newHoliday, setNewHoliday] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (q.data) setCal({ workingDays: q.data.calendar.workingDays, holidays: q.data.calendar.holidays }); }, [q.data]);
  const byYear = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const h of cal?.holidays ?? []) m.set(h.slice(0, 4), [...(m.get(h.slice(0, 4)) ?? []), h]);
    return [...m.entries()].sort(([a], [b]) => b.localeCompare(a));
  }, [cal?.holidays]);
  if (q.error) return <EstadoError error={q.error} />;
  if (!q.data || !cal) return <EstadoCargando />;

  const windowsOf = (d: number) => cal.workingDays.filter((w) => w.weekday === d);
  const setDay = (d: number, windows: Window[]) => setCal({ ...cal, workingDays: [...cal.workingDays.filter((w) => w.weekday !== d), ...windows] });
  const save = async () => {
    setSaving(true);
    try {
      await apiPut(`/entities/${slug}/settings/calendar`, cal);
      avisar.success('Calendario guardado. Las apps lo usan desde ahora.');
      await qc.invalidateQueries({ queryKey: ['entity-settings', slug] });
    } catch (e) {
      avisar.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {q.data.calendar.isDefault && <Alert variant="info"><Icono nombre="mdi:information-outline" /><AlertDescription>La entidad usa el calendario por defecto: lunes a viernes de 8:00 a 17:00 con los festivos de Colombia. Guárdalo para fijarlo o ajústalo.</AlertDescription></Alert>}
      <div className="grid gap-6 xl:grid-cols-2 [&>*]:min-w-0">
        <Card>
          <CardHeader>
            <CardTitle>Jornada</CardTitle>
            <CardDescription>Horas hábiles de cada día, en la zona horaria {q.data.calendar.timezone}. <Cambio info={q.data.changed.calendar} /></CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {WEEK.map((d) => {
              const windows = windowsOf(d);
              return (
                <div key={d} className="flex flex-wrap items-start gap-3 border-b border-border pb-3 last:border-0">
                  <label className="flex w-36 items-center gap-2 pt-1.5 text-sm"><Switch checked={windows.length > 0} onCheckedChange={(on) => setDay(d, on ? [{ weekday: d, start: '08:00', end: '17:00' }] : [])} />{WEEKDAY_LABEL[d]}</label>
                  <div className="flex-1 space-y-2">
                    {!windows.length && <span className="block pt-1.5 text-sm text-muted-foreground">No hábil</span>}
                    {windows.map((w, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Input type="time" className="tnum w-28" value={w.start} aria-label={`Inicio del ${WEEKDAY_LABEL[d]}`} onChange={(e) => setDay(d, windows.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)))} />
                        <span className="text-muted-foreground">a</span>
                        <Input type="time" className="tnum w-28" value={w.end} aria-label={`Fin del ${WEEKDAY_LABEL[d]}`} onChange={(e) => setDay(d, windows.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)))} />
                        {windows.length > 1 && <Button size="icon" variant="ghost" type="button" aria-label="Quitar franja" onClick={() => setDay(d, windows.filter((_, j) => j !== i))}><Icono nombre="mdi:close" /></Button>}
                      </div>
                    ))}
                    {windows.length > 0 && windows.length < 3 && <Button size="sm" variant="ghost" type="button" onClick={() => setDay(d, [...windows, { weekday: d, start: '14:00', end: '17:00' }])}><Icono nombre="mdi:plus" />Otra franja</Button>}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Festivos <Badge size="sm" variant="secondary" className="tnum">{cal.holidays.length}</Badge></CardTitle>
            <CardDescription>Días no hábiles aunque caigan en un día con jornada.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <SelectorFecha value={newHoliday} onChange={setNewHoliday} placeholder="Agregar un día" className="w-48" />
              <Button type="button" variant="outline" disabled={!newHoliday} onClick={() => { const iso = fechaAIso(newHoliday); if (iso) setCal({ ...cal, holidays: [...new Set([...cal.holidays, iso])].sort() }); setNewHoliday(null); }}><Icono nombre="mdi:plus" />Agregar</Button>
            </div>
            {!byYear.length ? <EstadoVacio titulo="Sin festivos" descripcion="Agrega los días no hábiles de la entidad." /> : byYear.map(([year, days]) => (
              <div key={year} className="space-y-2">
                <div className="text-sm font-medium tnum">{year}</div>
                <div className="flex flex-wrap gap-1.5">
                  {days.map((h) => (
                    <Badge key={h} variant="secondary" className="tnum gap-1">
                      {new Date(`${h}T12:00:00`).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', weekday: 'short' })}
                      <button type="button" className="text-muted-foreground hover:text-foreground" aria-label={`Quitar ${h}`} onClick={() => setCal({ ...cal, holidays: cal.holidays.filter((x) => x !== h) })}><Icono nombre="mdi:close" className="size-3" /></button>
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
      <Button onClick={() => void save()} disabled={saving}>{saving && <Spinner />}Guardar calendario</Button>
    </div>
  );
}
